import React, { useState, useMemo, useEffect, useRef, useCallback, useContext, forwardRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardBody } from "reactstrap";
import _ from 'lodash';
import queryString from 'query-string';

import Loader from './Loader';
import Notification from './Notification';
import customFields, { filterFields } from './customFields';
import Form from './customFields/Form';
import widgets from './customWidgets';

import useLoader from '../util/useLoader';
import { getObject, invertMap, mapObject, setObject } from '../util/mapObject';
import useTitle from '../util/useTitle';
import { useSideChannel, useSideChannelSubscription } from '../util/useSideChannel';
import ErrorListTemplate, { transformErrors } from "./ErrorListTemplate";
import clone from "../util/clone";
import useStateUpdateRequestPromise from "../util/useStateUpdateRequestPromise";
import ArrayFieldTemplate from './customFields/ArrayFieldTemplate/ArrayFieldTemplate';
import ObjectFieldTemplate from './customFields/ObjectFieldTemplate';
import parseUISchemaFromSchema from "../util/parseUISchemaFromSchema";
import Help from './Help';
import FormModal, { useOpenModal } from './FormModal';
import AppContext from '../context/AppContext';
import ModalContainer from './ModalContainer';
import Jnx, { useJnx } from '../util/jnx';
import SchemaFieldTemplate from './customFields/SchemaFieldTemplate';
import useJnxFormValidation from '../util/useJnxFormValidation';
import { AuthContext } from '../context/AuthContext';
import { mergeFlatListToErrorSchema, errorSchemaToFlatList } from '../util/formValidation';
import RequestReloadContext from '../context/RequestReloadContext';
import LookupApi from '../api/LookupApi';
import dfsSearch from '../util/dfsSearch';


/**
 * Callback for rendering a navigation component.
 * Receives a props object with the following documented attributes:
 * @callback renderNavigationCallback
 * @param {object} props
 * @param {string} props.title 
 * @param {any} props.navRef 
 * @param {any} props.formRef 
 * @param {boolean} props.hasUnsavedChanges
 * @returns {React.ElementType | null}
 */

/**
 * Callback for rendering a post submission component.
 * Receives a props object with the component's render props merged with the following documented attributes:
 * @callback renderFormSubmittedCallback
 * @param {object} props
 * @param {object} props.scope - The flat property mapping created in parseProps
 * @param {object} props.formDefinition - The definition object passed to this function
 * @returns {React.ElementType}
 */

/**
 * Callback for rendering the form component's children.
 * Receives a props object with the component's render props merged with the following documented attributes:
 * @callback renderFormChildrenCallback
 * @param {object} props
 * @param {object} props.scope - The flat property mapping created in parseProps
 * @param {object} props.formDefinition - The definition object passed to this function
 * @returns {React.ElementType}
 */

/** 
 * Higher level component for creating a form component customized by the given arguments. 
 * 
 * This is the component that renders the form specified by the UWE form definition JSON's schema.
 * The form schema defines the shape of the form, as nested JavaScript objects.
 * The shape of the schema determines the nested layout of the form.
 * Each level of the schema consititutes a kind of form component / field. Only those with types "object" and "array" have children objects.
 * - An array has an "items" child defined by the "items" property.
 * - An object has an arbitrary number of children defined by the "properties" property.
 * 
 * Built on top of {@link https://rjsf-team.github.io/react-jsonschema-form/docs/|react-jsonschema-form (jrsf)}.
 * 
 * The properties of the schema object consist of:
 * - The json-schema properties documented in {@link https://rjsf-team.github.io/react-jsonschema-form/docs/json-schema/|rjsf schema documentation}.
 * - The ui properties defined in {@link https://rjsf-team.github.io/react-jsonschema-form/docs/api-reference/uiSchema|rjsf uiSchema documentation}.
 * - Some UWE-specific properties and extensions. These will be documented soon.
 * For UWE, all of these properties are put in the same object, and separated later.
 * 
 * Each form component is dispatched according to the type, structure, and ui:field properties of its schema in the form schema.
 * 
 * @see {@link https://rjsf-team.github.io/react-jsonschema-form/docs/api-reference/|jrsf API Reference}
 * @see {@link https://json-schema.org/understanding-json-schema|json-schema specification}
 * 
 * @param {object} formDefinition - Definition object of the form component.
 * @param {string} formDefinition.displayName - Displayed component name in react component tree
 * @param {object} formDefinition.schema - A schema used to render the dynamic form (@see {@link https://rjsf-team.github.io/react-jsonschema-form/docs/json-schema|rjsf schema} )
 * @param {object} formDefinition.uiSchema - A uiSchema used to render the dynamic form (@see {@link https://rjsf-team.github.io/react-jsonschema-form/docs/api-reference/uiSchema|rjsf uiSchema} )
 * @param {function(object): object} formDefinition.parseProps - Callback that parses component props into a flat property mapping.
 *                 This mapping gets passed to the other callbacks.
 *                 Can also call React hooks and pass then in the mapping.
 * @param {function(object): promise} formDefinition.onCancel
 * @param {function(object): Promise} formDefinition.onSubmit - Callback that handles form submission. 
 *                 Receives rjsf's onSubmit argument and the parsed props as arguments, 
 *                 should return a promise that resolves if the submission was successful.
 * @param {function(object): promise} formDefinition.onChange
 * @param {string} formDefinition.validate - Optional callback that handles form validation. 
 *                 Receives rjsf's onSubmit argument and the parsed props as arguments.
 * @param {renderNavigationCallback | undefined} formDefinition.renderNavigation - Callback for rendering the navigation.
 * @param {renderFormChildrenCallback | undefined} formDefinition.renderFormChildren - Callback for rendering the form component's children.
 * @param {renderFormSubmittedCallback | undefined} formDefinition.renderFormSubmitted - Callback for rendering a post submission component.
 * 
 */
function FormComponent(formDefinition) {
    const {
        displayName = "FormComponent",
        title,
        parseProps = () => ({}),
        loadData = undefined,
        hideDataLoader,
        schema: fdSchema = {},
        uiSchema: fdUiSchemaProp = {},
        commentFieldsMap: fdCommentFieldsMap = null,
        buildFormContext = undefined,
        objectMap: fdObjectMap,
        customFormats = undefined,
        submitButtons: constSubmitButtons = null,
        alignButtons,
        onCancel,
        onSubmit: propOnSubmit,
        onChange: propOnChange,
        validate: propValidate,
        noHtml5Validate,
        beforeRenderHook = () => { },
        renderNavigation,
        renderFormDetails,
        renderFormSubmitted,
        renderFormChildren,
    } = formDefinition;

    const fdUiSchema = parseUISchemaFromSchema(fdSchema, fdUiSchemaProp);

    function parseUIUnique(schema) {
        const flattenedUiSchema = [];

        dfsSearch([['', schema]], ([path, current], { stack }) => {
            const pathPrefix = path.length ? `${path}.` : '';

            Object.entries(current || {}).forEach(([attr, value]) => {
                if (attr.startsWith("akc:unique")) {
                    flattenedUiSchema.push(`${pathPrefix.slice(0, -1)}`);
                }
            });

            switch (current?.type) {
                case "object":
                    stack.push(...Object.entries(current.properties).map(
                        ([prop, propSchema]) => [`${pathPrefix}${prop}`, propSchema]
                    ));
                    break;
                case "array":
                    stack.push([`${pathPrefix}items`, current.items]);
                    break;
                default: break;
            }
        });

        return flattenedUiSchema;
    }

    const fdUiUnique = parseUIUnique(fdSchema);

    let {
        staticFormData
    } = formDefinition;

    const staticUnmappedFormData = staticFormData;

    if (staticFormData && fdObjectMap) {
        staticFormData = mapObject(staticFormData, invertMap(fdObjectMap));
    }

    const staticEffects = [
        // title && (() => { useTitle(title) })
    ];

    function Component(props) {
        const currentFormDef = useRef();
        const currentFormData = useRef();
        const navRef = useRef();
        const formRef = useRef();
        const formComponentRef = useRef();
        const submitState = useRef();
        const extraErrors = undefined;
        const setExtraErrors = useCallback(extraErrorSchema => {
            formComponentRef.current?.setState(st => ({
                errorSchema: mergeFlatListToErrorSchema([
                    ...errorSchemaToFlatList(st.errorSchema || {}),
                    ...errorSchemaToFlatList(extraErrorSchema)
                ])
            }));
        }, [formComponentRef]);

        const rrc = useContext(RequestReloadContext);

        const [loadingAction, loadingError, loadFn] = useLoader();
        const [formSubmitted, setFormSubmitted] = useState();
        const [formSubmitResult, setFormSubmitResult] = useState();
        const [errorLoadingInitialFormData, setErrorLoadingInitialFormData] = useState();
        const [loadingInitialFormData, setLoadingInitialFormData] = useState();
        const [initialFormObject, setInitialFormObject] = useState(staticUnmappedFormData || {});
        const [initialFormData, setInitialFormData] = useState(staticFormData);

        const loading = loadingAction || (loadingInitialFormData && !hideDataLoader);
        const error = loadingError || errorLoadingInitialFormData;
        const [
            _formDefinition,
            _setFormDefinition
        ] = useState([fdSchema, fdUiSchema, fdObjectMap, fdCommentFieldsMap, 0]);
        const [schema, uiSchema, objectMap, commentFieldsMap, curClientIdx] = _formDefinition;

        const sideChannel = useSideChannel(initialFormData);

        currentFormDef.current = [schema, uiSchema, objectMap];
        const { onLocateField } = props;


        const parsedProps = { ...props, ...parseProps(props) };
        const propsSignature = JSON.stringify(parsedProps);

        const scope = useMemo(() => ({
            props: parsedProps,
            initialFormObject,
            initialFormData,
        }), [
            propsSignature,
            initialFormObject,
            initialFormData
        ]);

        const setFormDataValues = useCallback(dataValues => {
            const formC = formComponentRef.current;
            formC.setState(
                prevState => ({ formData: clone.set(prevState.formData, dataValues) }),
                () => formC.props.onChange && formC.props.onChange(formC.state)
            );
        }, [formComponentRef]);

        const openModal = useOpenModal();
        const auth = useContext(AuthContext);

        const navigate = useNavigate();
        const location = useLocation();

        const query = useMemo(() => (
            queryString.parse(location.search || "")
        ), [location.search]);

        /**
         * THIS DOCUMENTATION IS PRELIMINARY, SHOULD BE CONVERTED TO JSDOC OR TS DECLARATION!
         * 
         * The object which contains the form's context, to be used in Jnx expressions inside the form definition json.
         * 
         * This includes:
         * - sideChannel: A pub/sub event channel, used to subscribe to and publish actions.
         * - openModal: A callback used to open a modal. The input is the name of a key in the global MODALS object (src/blc/modals/index.js).
         * - auth: The application's AuthContext, used to query data relevant to the session.
         * - navigate: The application's react-router navigate obj.
         * - rrc: The RequestReloadContext. Includes a reload function.
         * - onLocateField: WIP
         * - formDefinition: An object containing information relevant to the form definition.
         *     - schema: The form schema, including UWE properties.
         *     - uiSchema: The form ui schema.
         *     - objectMap: A mapping from formData to form fields defined in schema.
         *     - commentFieldsMap: WIP
         *     - curClientIdx: WIP
         *     - invObjectMap: The inverse of objectMap. Can be `undefined`.
         * - setFormDataValues: A setter which allows setting the values for the formData.
         * - formFields.current: An object containing a mapping a schema property name to a boolean. Initial value of {}.
         * 
         * Additionally, more properties are added by the following factory function passed as a prop to the form:
         * - buildFormContext: A function which generates an object with extra formContext properties. By default `undefined`.
         * 
         * A buildFormContext is passed in from UWEEntityFormComponent, which provides:
         * - user: An object that contains information about the logged in user. Should be the same as auth.user.
         * - integration: An object containing two fetching functions of the IntegrationsApi:
         *     - url: IntegrationsApi.GetOperationUrl
         *     - call: IntegrationsApi.CallOperationUrl
         * - draftId: WIP
         * - status: WIP
         * - fileField: WIP
         * - clientEmail: The client email.
         * - clientFirstName: The client first name.
         * - clientLastName: The client last name.
         * 
         * This formContext and the formData get passed to each individual field. Each field can use the
         * provided data and utility functions in its code or bind it in a Jnx environment.
         * 
         * At the moment, it is the responsibility of each field to ensure Jnx expressions are evaluated with
         * this formContext bound to the Jnx environment if needed.
         */
        const formContext = useMemo(() => ({
            sideChannel,
            openModal,
            auth,
            navigate,
            location,
            query,
            rrc,
            onLocateField,
            formDefinition: {
                schema,
                uiSchema,
                objectMap,
                commentFieldsMap,
                curClientIdx,
                invObjectMap: objectMap ? invertMap(objectMap) : undefined,
            },
            setFormDataValues,
            formFields: { current: {} },
            ...(buildFormContext ? buildFormContext(scope) : {})
        }), [
            buildFormContext, rrc, scope, sideChannel, setFormDataValues, openModal, auth, _formDefinition,
            onLocateField,
            navigate,
            location,
            query
        ]);

        const getCurrentFormObject = useCallback(() => {
            const objectMap = currentFormDef.current[2];
            const cfo = getFormObjectFromData(currentFormData.current, objectMap, initialFormObject, formContext);
            return cfo;
        }, [currentFormData, initialFormObject, formContext]);

        const setFormObject = useCallback(formObject => {
            const objectMap = currentFormDef.current[2];
            const formData = getFormDataFromObject(formObject, objectMap, formContext);
            setInitialFormData(formData);
            setInitialFormObject(formObject);
            sideChannel.publish(formData, formObject);
        }, [sideChannel, formContext]);

        const saveCurrentFormObject = useStateUpdateRequestPromise(
            initialFormObject, setFormObject, getCurrentFormObject
        );


        const {
            readonly,
            submitButtons: propSubmitButtons
        } = parsedProps;

        const setFormDefinition = useCallback((schema, uiSchema, objectMap, commentFieldsMap, clientIdx) => {
            schema = schema || fdSchema;
            uiSchema = uiSchema || fdUiSchema;
            objectMap = objectMap || fdObjectMap;
            commentFieldsMap = commentFieldsMap || fdCommentFieldsMap;
            _setFormDefinition([schema, uiSchema, objectMap, commentFieldsMap, clientIdx || 0]);
            if (initialFormObject) {
                const formData = getFormDataFromObject(initialFormObject, objectMap, formContext);
                setInitialFormData(formData);
                currentFormData.current = formData;
                sideChannel.publish(formData, initialFormObject);
            }
        }, [
            fdSchema,
            formContext,
            initialFormObject,
            fdUiSchema,
            fdObjectMap,
        ]);

        staticEffects.forEach(fn => !!fn && fn());

        const renderScope = useMemo(() => ({
            ...scope,
            loading, error, loadFn
        }), [scope, loading, error, loadFn]);

        const submitButtons = propSubmitButtons || constSubmitButtons;
        const sortedSubmitButtons = useMemo(() => {
            if (!submitButtons) return null;
            const list = Object.entries(submitButtons);
            list.sort((a, b) => (
                a[1].order - b[1].order
            ))
            return list;
        }, [submitButtons]);

        const onSubmit = useMemo(() => {

            const timestamp = new Date().getTime();

            const fn = readonly ? (() => { }) : async (args) => {

                const submitButton = (submitButtons || {})[submitState.current] || {};
                const { formData } = args;

                const submitAction = async () => {
                    const formObject = getFormObjectFromData(formData, objectMap, initialFormObject, formContext);
                    if (formObject) {
                        args.object = formObject;
                        args.clientIdx = curClientIdx;
                        args.commentFieldsMap = commentFieldsMap;
                        args.generateDocuments = parsedProps.generateDocuments;
                        if (submitButton.onProcessSubmit) {
                            submitButton.onProcessSubmit(args, scope);
                        } else if (submitButton.setProps && formObject) {
                            executeSetProps(submitButton.setProps, formObject, formData, {
                                formData, formContext, formObject
                            });
                        }
                    }

                    setInitialFormData(formData);
                    setInitialFormObject(formObject);
                    const result = await propOnSubmit(args, scope);
                    setFormSubmitResult(result);
                    setFormSubmitted(true);
                };

                if (submitButton.onBeforeSubmit) {
                    const evt = {
                        preventDefault() { evt.cancel = true },
                        setExtraErrors,
                        formData,
                        formContext,
                        scope,
                    };

                    await submitButton.onBeforeSubmit(evt);
                    if (evt.cancel) return;
                }

                if (!(await validateSubmission())) {
                    return;
                 }
                 
                return (submitButton.modal ?
                    openModal(...submitButton.modal, formData, submitButton.setProps) :
                    (submitButton.confirm ?
                        openModal("ConfirmationModal", submitButton.text, ...submitButton.confirm) :
                        Promise.resolve(true))
                    ).then(confirmation => {
                        return confirmation ? loadFn(submitAction) : undefined;
                    });
            }

            fn.timestamp = timestamp;
            return fn;
        }, [readonly, propOnSubmit, setFormSubmitted, scope, initialFormObject, setExtraErrors]);

        const jnxValidation = useJnxFormValidation(schema, uiSchema);

        const validate = useMemo(() => {
            function validate(formData, errors) {
                console.log('running validate');

                [jnxValidation, propValidate].reduce((errors, valFn) => {
                    return valFn ? valFn(formData, errors, scope, formContext.formFields?.current) : errors;
                }, errors);

                return errors;
            }

            return (propValidate || jnxValidation) ? validate : undefined;
        }, [propValidate, jnxValidation, scope])

        useEffect(() => {
            const flags = {};
            async function initialFormDataLoader(parsedProps) {
                setErrorLoadingInitialFormData();
                setLoadingInitialFormData(true);
                try {
                    const formObject = await loadData(parsedProps);
                    if (!flags.canceled) {
                        setFormObject(formObject);
                    }
                } catch (e) {
                    if (!flags.canceled) setErrorLoadingInitialFormData(e.message);
                }
                setLoadingInitialFormData();
            }

            if (loadData) {
                initialFormDataLoader(parsedProps);
                return () => {
                    // cancel load if component is unloaded or props change
                    flags.canceled = true;
                }
            }
            return undefined;
        }, [
            loadData,
            propsSignature,
        ]);
        
        const propagateChangeRef = useRef();
        const onChange = useMemo(() => {
            const propagateChange = _.debounce(() => {
                // kill any old propagations
                if (propagateChangeRef.current !== propagateChange) { return; }
                const formData = currentFormData.current;
                const formObject = getCurrentFormObject();
                sideChannel.publish(formData, formObject);

                if (navRef && navRef.current) {
                    navRef.current.updateNav();
                }
            }, 200);
            propagateChangeRef.current = propagateChange;

            return ({ formData }) => {
                currentFormData.current = formData;
                propagateChange();
            }
        }, [sideChannel, currentFormData, getCurrentFormObject, navRef]);

        useEffect(() => propOnChange ? sideChannel?.subscribe((formData, formObject) => {
            propOnChange({ formData, formObject, parsedProps });
        }) : undefined, [propOnChange, sideChannel, parsedProps]);


        useEffect(() => {
            if (navRef && navRef.current) {
                navRef.current.updateNav();
            }
        }, [initialFormData]);

        const onSubmitRef = useRef();
        onSubmitRef.current = onSubmit;

        async function validateSubmission() {

            const errors = {};
            let hasErrors = false;

            await Promise.all(Object.entries(fdUiUnique || []).map(async ([_, obj]) => {
                const path = obj;
                const key = path.split('.');
                const property = key[key.length - 1];

                const targetValue = getObject(currentFormData.current, path);
                const id = initialFormObject?.id || 0;

                if (targetValue) {
                    try {
                        const response = await LookupApi.searchData({ property, targetValue, id });
                        if (response > 0) {
                            hasErrors = true;
                            addErrorIfNotExists(errors, formComponentRef.current?.state?.errorSchema, path, "debe ser único");
                        }
                    } catch (ex) {
                        hasErrors = true;
                        addErrorIfNotExists(errors, formComponentRef.current?.state?.errorSchema, path, "error al validar");
                        console.log(ex);
                    }
                }
            }));

            if (hasErrors) setExtraErrors(errors);

            return !hasErrors;
        }

        function makeSubmitButtonHandler({
            key,
            ignoreValidation,
        }) {
            async function submitButtonHandler(event) {
                submitState.current = key;

                const onSubmit = onSubmitRef.current;
                const pathToRedirect = submitButtons?.[key]?.pathToRedirect || "";
                if(submitButtons?.[key]?.cancelForm) onCancel({formContext, pathToRedirect});

                if (ignoreValidation) {
                    if (event) event.preventDefault();
                    onSubmit({ formData: currentFormData.current });
                } else if (!(await validateSubmission())) {
                    event.preventDefault();
                }
            };

            return submitButtonHandler;
        }
        
        const brh = beforeRenderHook({
            parsedProps,
            sideChannel,
            formComponentRef,
            setFormDataValues,
            setFormDefinition,
            saveCurrentFormObject,
            setFormObject,
            setExtraErrors,
            navRef,
            onSubmitRef,
            getCurrentFormObject,
            initialFormObject,
            currentFormData,
            submitButtons,
        });

        const processed_uiSchema = useMemo(() => {
            const pschema = {};
            const stack = Object.entries(uiSchema).map(([k, v]) => [pschema, k, v]);
            while (stack.length) {
                const current = stack.pop();
                const [obj, k, v] = current;
                let vvalue = v;

                if (k === "ui:help" && v) vvalue = <Help>{v}</Help>;

                obj[k] = vvalue;

                if (v && typeof v === 'object' && !Array.isArray(v)) {
                    stack.push(...Object.entries(v).map(([k, v2]) => [v, k, v2]));
                }
            }
            return pschema;
        }, [uiSchema]);

        const transformErrorsCb = useCallback((errors) => transformErrors(errors, schema), [schema]);

        useEffect(() => {
            // console.log("formComponentRef.current.state", formComponentRef?.current?.state?.formData);
            currentFormData.current = formComponentRef?.current?.state?.formData;
        }, [formComponentRef?.current?.state?.formData]);

        const formClassName = processed_uiSchema?.formClassName || "";
        const forceGoBackToDashboard = (submitButtons || {})[submitState.current]?.forceGoBackToDashboard || false;
        const pathToRedirect = (submitButtons || {})[submitState.current]?.pathToRedirect || "";

        return (
            <>
                {loading ? <Loader fullscreen /> : null}
                {error ? <Notification color="danger" show="true" >{error}</Notification> : null}
                {(formSubmitted && renderFormSubmitted) ? (
                    renderFormSubmitted({
                        ...props,
                        navigate,
                        location,
                        submitState: submitState.current,
                        scope: renderScope,
                        formDefinition,
                        formSubmitResult,
                        forceGoBackToDashboard,
                        pathToRedirect,
                    })
                ) : (
                    <>
                        {renderNavigation ? renderNavigation({ title, navRef, formRef }) : null}
                        <div className={`form-component ${formClassName}`} ref={formRef}>
                            {renderFormDetails ? renderFormDetails({
                                brh,
                                parsedProps,
                                setFormObject,
                                sideChannel
                            }) : null}
                            <Card>
                                <CardBody>
                                    <ModalContainer
                                        formContext={formContext}
                                        currentFormData={currentFormData}
                                        getCurrentFormObject={getCurrentFormObject}
                                        scope={scope}
                                    />
                                    <Form
                                        ref={formComponentRef}
                                        schema={schema}
                                        ArrayFieldTemplate={ArrayFieldTemplate}
                                        ObjectFieldTemplate={ObjectFieldTemplate}
                                        FieldTemplate={SchemaFieldTemplate}
                                        transformErrors={transformErrorsCb}
                                        disabled={readonly}
                                        uiSchema={processed_uiSchema}
                                        customFormats={customFormats}
                                        fields={customFields}
                                        // formData={currentFormData.current || initialFormData}
                                        formData={initialFormData}
                                        formContext={formContext}
                                        noHtml5Validate={noHtml5Validate}
                                        onSubmit={onSubmit}
                                        extraErrors={extraErrors}
                                        validate={validate}
                                        onChange={loadingInitialFormData ? undefined : onChange}
                                        widgets={widgets}
                                        ErrorList={ErrorListTemplate}
                                    ><>
                                            {renderFormChildren ? renderFormChildren({
                                                ...props,
                                                scope,
                                                formDefinition,
                                            }) : null}
                                            {!readonly && sortedSubmitButtons ? (<div className={`form-submit-buttons ${alignButtons ? `float-${alignButtons}` : ''}`}>
                                                {sortedSubmitButtons.map(([key, btnDef]) => <SubmitButton key={key}
                                                    onClick={makeSubmitButtonHandler({ key, ...btnDef })}
                                                    btnDef={btnDef}
                                                    initialFormObject={initialFormObject}
                                                    formContext={formContext}
                                                />)}
                                            </div>) : null}
                                        </></Form>
                                </CardBody>
                            </Card></div>
                    </>
                )}
            </>
        );
    }

    Component.displayName = displayName;
    Component.formDefinition = formDefinition;

    return Component
}


function executeSetProps(setProps, formObject, formData, bindings) {
    Object.entries(setProps || {}).forEach(([attr, expr]) => {
        const value = new Jnx(expr).eval(formData, '', bindings);
        console.log('executeSetProps formObject[attr] = value;', attr, value);
        formObject[attr] = value;
    });
}


function SubmitButton({
    onClick,
    btnDef,
    initialFormObject,
    formContext,
}) {
    const {
        text: propText,
        className,
        Component = "button",
        btnProps,
        'ui:showIf': showIf,
        'ui:disableIf': disableIf,
        setProps,
        ignoreValidation,
        onBeforeSubmit,
        type = "submit",
        ...props
    } = btnDef;

    const { sideChannel } = formContext;
    const rootFormData = useSideChannelSubscription(sideChannel, [0]);

    const showIfJnx = useJnx(showIf);
    const disableIfJnx = useJnx(disableIf);
    const textJnx = useMemo(() => propText && propText.expr ? new Jnx(propText) : null, [propText]);
    const text = useMemo(() => (
        `${(textJnx ? textJnx.eval(rootFormData, '') : propText)}` || 'Someter'
    ), [propText, textJnx, rootFormData]);

    const show = useMemo(() => showIfJnx ? showIfJnx.eval(
        rootFormData, '', {
        formData: rootFormData,
        initialFormObject,
        formContext
    }) : true, [showIfJnx, rootFormData, initialFormObject, formContext]);

    const disabled = useMemo(() => disableIfJnx ? disableIfJnx.eval(
        rootFormData, '', {
        formData: rootFormData,
        initialFormObject,
        formContext
    }) : false, [disableIfJnx, rootFormData, initialFormObject, formContext]);

    return show ? (<Component
        type={type}
        onClick={onClick}
        disabled={disabled}
        {...(typeof Component === 'string' ? {} : { sideChannel })}
        className={`btn ${className || ''}`}
        data-cy={`btn-${text}`}
        {...(props || {})}
    >{text}</Component>) : null;
}


function getFormObjectFromData(formData, objectMap, initialFormObject, context) {
    return objectMap ? mapObject(
        formData,
        objectMap,
        JSON.parse(JSON.stringify(initialFormObject || {})),
        context
    ) : undefined;
}

function getFormDataFromObject(formObject, objectMap, context) {
    return objectMap ? mapObject(
        formObject,
        invertMap(objectMap),
        undefined,
        context
    ) : formObject;
}


export const ExtendedForm = forwardRef((props, ref) => {
    return (<Form
        {...props}
        ref={ref}
        fields={customFields}
        transformErrors={transformErrors}
        widgets={widgets}
        ErrorList={ErrorListTemplate}
    />);
});

function errorExist(errorSchema, path, errorMessage) {
    const response = getObject(errorSchema, `${path}.__errors`)?.filter(x => x === errorMessage) || [];
    return response.length;
}

function addErrorIfNotExists(errors, currentErrors, path, errorMessage) {
    const hasError = errorExist(currentErrors, path, errorMessage);
    if (!hasError) setObject(errors, `${path}.__errors`, [errorMessage]);
}

export const FilterForm = forwardRef((props, ref) => {
    return (
        <Form
            {...props}
            ref={ref}
            fields={{...customFields, ...filterFields}}
        />
    )
})

export default FormComponent;